import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AiOutlineSearch, AiOutlineClose } from "react-icons/ai";
import { FiCheck } from "react-icons/fi";
import styles from "./UserSearchSelect.module.scss";

/**
 * Combobox de recherche/selection d'utilisateur(s), reutilisable.
 *
 * Props :
 *  - allUser        : tableau des utilisateurs [{ ID, display_name, user_email, user_login }]
 *  - selectedIds    : tableau d'IDs selectionnes (toujours un tableau)
 *  - onChange(ids)  : callback avec le nouveau tableau d'IDs
 *  - multiSelect    : true = cases a cocher (selection multiple), false = selection unique
 */
const UserSearchSelect = ({
  allUser = [],
  selectedIds = [],
  onChange,
  multiSelect = false,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0, width: 0 });
  const wrapperRef = useRef(null);
  const searchBoxRef = useRef(null);
  const dropdownRef = useRef(null);

  // Positionne le dropdown (rendu en portail) juste sous le champ de recherche
  const updateCoords = useCallback(() => {
    if (!searchBoxRef.current) return;
    const rect = searchBoxRef.current.getBoundingClientRect();
    setCoords({
      left: rect.left,
      top: rect.bottom + 4,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateCoords();
    window.addEventListener("scroll", updateCoords, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      window.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open, updateCoords]);

  // Fermer le dropdown au clic exterieur (wrapper + dropdown en portail)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase().trim();
    const list = q
      ? allUser.filter(
          (u) =>
            (u.display_name || "").toLowerCase().includes(q) ||
            (u.user_email || "").toLowerCase().includes(q) ||
            (u.user_login || "").toLowerCase().includes(q) ||
            u.ID.toString().includes(q)
        )
      : allUser;
    return [...list].sort((a, b) =>
      (a.user_login || "").localeCompare(b.user_login || "")
    );
  }, [allUser, search]);

  const selectedUsers = useMemo(
    () => allUser.filter((u) => selectedIds.includes(u.ID)),
    [allUser, selectedIds]
  );

  const isSelected = (id) => selectedIds.includes(id);

  const toggleUser = (id) => {
    if (multiSelect) {
      if (isSelected(id)) {
        onChange(selectedIds.filter((x) => x !== id));
      } else {
        onChange([...selectedIds, id]);
      }
    } else {
      onChange([id]);
      setOpen(false);
      setSearch("");
    }
  };

  const removeUser = (id) => onChange(selectedIds.filter((x) => x !== id));

  const selectAllVisible = () => {
    const visibleIds = filteredUsers.map((u) => u.ID);
    const merged = Array.from(new Set([...selectedIds, ...visibleIds]));
    onChange(merged);
  };

  const clearAll = () => onChange([]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.searchBox} ref={searchBoxRef}>
        <AiOutlineSearch className={styles.searchIcon} />
        <input
          type="text"
          className={styles.searchInput}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t("common.search_user_placeholder")}
        />
        {search && (
          <AiOutlineClose
            className={styles.clearSearch}
            onClick={() => setSearch("")}
          />
        )}
      </div>

      {open &&
        createPortal(
          <div
            className={styles.dropdown}
            ref={dropdownRef}
            style={{
              left: coords.left,
              top: coords.top,
              width: coords.width,
            }}
          >
            {multiSelect && filteredUsers.length > 0 && (
              <div className={styles.dropdownActions}>
                <button type="button" onClick={selectAllVisible}>
                  {t("preset.select_all_visible")}
                </button>
                {selectedIds.length > 0 && (
                  <button type="button" onClick={clearAll}>
                    {t("preset.clear_selection")}
                  </button>
                )}
              </div>
            )}

            {filteredUsers.length === 0 ? (
              <div className={styles.noResult}>
                {t("common.no_option_available")}
              </div>
            ) : (
              <ul className={styles.list}>
                {filteredUsers.map((u) => (
                  <li
                    key={u.ID}
                    className={`${styles.item} ${
                      isSelected(u.ID) ? styles.itemSelected : ""
                    }`}
                    onClick={() => toggleUser(u.ID)}
                  >
                    {multiSelect && (
                      <span className={styles.checkbox}>
                        {isSelected(u.ID) && <FiCheck />}
                      </span>
                    )}
                    <span className={styles.itemMain}>{u.user_login}</span>
                    <span className={styles.itemSub}>
                      {u.display_name} · {u.user_email}
                    </span>
                    <span className={styles.itemId}>#{u.ID}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>,
          document.body
        )}

      {selectedUsers.length > 0 && (
        <div className={styles.badges}>
          {selectedUsers.map((u) => (
            <span key={u.ID} className={styles.badge}>
              <span className={styles.badgeText}>
                {u.user_login} <em>({u.ID})</em>
              </span>
              <AiOutlineClose
                className={styles.badgeRemove}
                onClick={() => removeUser(u.ID)}
              />
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserSearchSelect;
